import React, { useCallback, useEffect, useState } from 'react';
import { Vehicle } from 'core/models/vehicle';
import { VideoPlayer } from 'components/containers/common/VehicleMonitor/VideoPlayer';
import { useChannelLayout } from 'components/containers/common/VehicleMonitor/useChannelLayout';
import { Project } from 'core/models/project';
import { Environment } from 'core/models/environment';
import { SubScreenLayout } from 'core/models/layout';
import styled from '@emotion/styled';

const PlayerFrame = styled.div({
    position: 'relative',
    width: '100%',
    height: '100%',
});

const PlayerLabel = styled.div({
    position: 'absolute',
    top: '1em',
    left: '1em',
    color: 'white',
});

function TagLabel(props: { tag: string }) {
    const { tag } = props;

    const [visible, setVisible] = useState(true);
    useEffect(() => {
        setVisible(true);
        setInterval(() => {
            setVisible(false);
        }, 5000);
    }, [tag]);

    return <PlayerLabel hidden={!visible}>{tag}</PlayerLabel>;
}

function useBuildPlayer(vehicleId: Vehicle.Id, projectId: Project.Id, environmentId: Environment.Id) {
    const viewOnly = true;

    const channelLayout = useChannelLayout(vehicleId, projectId, environmentId);

    const BuildPlayer = useCallback((tag: string | undefined) => {
        if (!channelLayout) return undefined;
        if (!tag) return undefined;

        const channel = channelLayout.cameras.get(tag);
        return channel
            ? <PlayerFrame>
                <VideoPlayer
                    channel={channel}
                    projectId={projectId}
                    environmentId={environmentId}
                    viewOnly={viewOnly}
                    mirror={false}
                    upsidedown={false}
                    stretch={true}
                    audio={false}
                />
                <TagLabel tag={tag} />
            </PlayerFrame>
            : undefined;
    }, [channelLayout, environmentId, projectId, viewOnly]);

    return BuildPlayer;
}

export type RemoteMonitorReadyProps = {
    projectId: Project.Id;
    vehicleId: Vehicle.Id;
    environmentId: Environment.Id;
    layout: SubScreenLayout | undefined;
}

function RemoteMonitorReady(props: RemoteMonitorReadyProps) {
    const { projectId, vehicleId, environmentId, layout: displayLayout } = props;

    const buildPlayer = useBuildPlayer(vehicleId, projectId, environmentId);

    const channels = displayLayout
        ? {
            center: buildPlayer(displayLayout.center[0]?.tag),
        }
        : undefined;

    return (
        <React.Fragment>
            {
                channels?.center ?? <div>no video</div>
            }
        </React.Fragment>
    );
}

type RemoteMonitorProps = {
    projectId: Project.Id | undefined;
    vehicleId: Vehicle.Id | undefined;
    environmentId: Environment.Id | undefined;
    layout: SubScreenLayout | undefined;
}

export function RemoteMonitor(props: RemoteMonitorProps) {
    const { projectId, vehicleId, environmentId, layout } = props;

    return projectId && vehicleId && environmentId && layout
        ? <RemoteMonitorReady projectId={projectId} vehicleId={vehicleId} environmentId={environmentId} layout={layout} />
        : <div>RemoteMonitor not ready: {JSON.stringify({ projectId, vehicleId, environmentId, layout })}</div>
        ;
}
